import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getPricingTierById, updatePricingTierById } from '../../utils/api/pricingTier';
import { toast } from 'sonner';

function EditPricingTierModal({ id, show, onClose }) {
  const [formData, setFormData] = useState({
    label: '',
    minMinutes: '',
    maxMinutes: '',
    ratePerMinute: '',
    isActive: true
  });

  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id || !show) return;
    setFetching(true);
    getPricingTierById(id)
      .then((res) => {
        const data = res?.data ?? res ?? {};
        setFormData({
          label: data.label ?? '',
          minMinutes: data.minMinutes ?? '',
          maxMinutes: data.maxMinutes ?? '',
          ratePerMinute: data.ratePerMinute ?? '',
          isActive: data.isActive ?? false,
        });
      })
      .catch((err) => {
        console.error('Error loading pricing tier:', err);
        toast.error(err?.message || 'Failed to load tier for editing.');
        onClose?.();
      })
      .finally(() => setFetching(false));
  }, [id, show, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { label, minMinutes, maxMinutes, ratePerMinute, isActive } = formData;

    // Treat empty-string as missing; allow 0
    if (
      (label ?? '').toString().trim() === '' ||
      minMinutes === '' ||
      maxMinutes === '' ||
      ratePerMinute === ''
    ) {
      toast.error('All fields are required.');
      return;
    }

    const payload = {
      label: label.trim(),
      minMinutes: Number(minMinutes),
      maxMinutes: Number(maxMinutes),
      ratePerMinute: Number(ratePerMinute),
      isActive: !!isActive,
    };

    try {
      setSaving(true);
      const res = await updatePricingTierById(id, payload);
      toast.success(res?.message || 'Pricing tier updated successfully!');
      onClose?.();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err?.message || 'Failed to update pricing tier.');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <motion.div
      className="modal fade show"
      tabIndex="-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        display: 'block',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1050,
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <div className="modal-header">
            <h5 className="modal-title">Edit Pricing Tier</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {fetching ? (
                <div className="text-center flex gap-10">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="shimmer shimmer-text" style={{ width: '140px', height: '2.5rem', margin: '8px auto' }} />
                  ))}
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label fw-semibold">Label*</label>
                    <input
                      type="text"
                      name="label"
                      className="form-control"
                      value={formData.label}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Minimum Minutes*</label>
                    <input
                      type="number"
                      name="minMinutes"
                      className="form-control"
                      value={formData.minMinutes}
                      onChange={handleChange}
                      min={0}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Maximum Minutes*</label>
                    <input
                      type="number"
                      name="maxMinutes"
                      className="form-control"
                      value={formData.maxMinutes}
                      onChange={handleChange}
                      min={1}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Rate Per Minute (₹)*</label>
                    <input
                      type="number"
                      step="0.01"
                      name="ratePerMinute"
                      className="form-control"
                      value={formData.ratePerMinute}
                      onChange={handleChange}
                      min={0}
                      required
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold d-block">Active Status</label>
                    <div className="d-flex align-items-center">
                      <label
                        className="switch"
                        style={{ position: 'relative', display: 'inline-block', width: '50px', height: '24px', marginBottom: 0 }}
                      >
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={!!formData.isActive}
                          onChange={handleChange}
                          style={{ opacity: 0, width: 0, height: 0 }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            cursor: 'pointer',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: formData.isActive ? '#28a745' : '#ccc',
                            transition: '.4s',
                            borderRadius: '34px'
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              height: '18px',
                              width: '18px',
                              left: formData.isActive ? '26px' : '4px',
                              top: '3px',
                              backgroundColor: 'white',
                              transition: '.4s',
                              borderRadius: '50%'
                            }}
                          />
                        </span>
                      </label>
                      <span className="ms-3 fw-semibold" style={{ minWidth: '60px' }}>
                        {formData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer d-flex justify-content-between">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={saving}>
                {saving ? 'Saving...' : 'Update Tier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}

export default EditPricingTierModal;
